/**
 * This module exports functions useful for handling settings.
 */

import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';

import * as K from 'common/constants';
import { Settings, UrlParams } from 'common/types';
import { DisplaySettings, SystemSettings } from 'common/graphqlTypes';
import UrlParamsByRoute from 'common/params';
import { resolveParamValue } from 'common/util/string';
import { buildUrl, getScalyrPath } from 'common/util/location';

export type ServerSettings = SystemSettings & DisplaySettings;

/**
 * Processes settings data from the server by merging it into client settings.
 *
 * @param curSettings     current client settings
 * @param settingsData    settings data from the server (display settings, system settings, or both merged)
 *
 * @return new client settings with the server settings merged in
 */
export const processServerSettings = (curSettings: Settings, settingsData: ServerSettings): Settings => {
  const newSettings: Settings = cloneDeep(curSettings);
  Object.entries(K.SERVER_SETTING_NAME).forEach(([key, value]) => {
    const newValue = settingsData[value as keyof ServerSettings];
    if (newValue != null) {
      newSettings[key as K.SETTING] = newValue;
    }
  });

  return newSettings;
};

/**
 * Apply settings taken from URL parameters to the given settings object. The params act as overrides.
 *
 * @param settings    a settings object
 * @param params      URL params
 *
 * @return a new settings object with the URL params applied
 */
export const applyParamsToSettings = (settings: Settings, params: UrlParams): Settings => {
  const newSettings: Settings = cloneDeep(settings);
  Object.keys(K.URL_PARAM_TO_SETTING).forEach((param) => {
    const setting = K.URL_PARAM_TO_SETTING[param as K.URL_PARAM];
    const paramValue = params[param as K.URL_PARAM];
    if (setting && paramValue) {
      let settingValue = resolveParamValue(paramValue, isArray(K.DEFAULT_SETTINGS[setting]));
      if (param === K.URL_PARAM.SYSTEM_PREFS) {
        // field names in setting don't quite match what can appear in legacy URL; rawlogline => rawLogLine, source => host
        settingValue = (settingValue as string[]).map((field) => {
          if (field.toLowerCase() === 'rawlogline') {
            return 'rawLogLine';
          }
          if (field === 'source') {
            return 'host';
          }
          return field;
        });
      }
      newSettings[setting] = settingValue;
    }
  });

  return newSettings;
};

/**
 * Adds params to a URL for any settings associated with that URL.
 *
 * @param url         a full URL
 * @param settings    a settings object
 *
 * @returns updated URL with its settings populated
 */
export const applySettingsToUrl = (url: string, settings: Settings): string => {
  const route = getScalyrPath(new URL(url));
  const paramsObj = UrlParamsByRoute[route];
  const routeParams = paramsObj
    ? [...paramsObj.eventParamList, ...paramsObj.viewParamList, ...paramsObj.disposableParamList]
    : [];

  const paramsToSet = {} as UrlParams;
  Object.keys(K.URL_PARAM_TO_SETTING).forEach((param) => {
    const setting = K.URL_PARAM_TO_SETTING[param as K.URL_PARAM] as K.SETTING;
    const settingValue = settings[setting];
    if (routeParams.includes(param as K.URL_PARAM) && settingValue != null) {
      paramsToSet[param as K.URL_PARAM] = isArray(settingValue) ? JSON.stringify(settingValue) : settingValue;
    }
  });

  return buildUrl(url, paramsToSet);
};

/**
 * Given a set of params, returns those for display settings which have a non-empty value.
 *
 * @param params    a set of URL params
 * @returns a set of non-empty URL params with associated display settings
 */
export const getDisplaySettingsFromParams = (params: UrlParams): UrlParams => {
  const settingsParams = {} as UrlParams;

  K.DISPLAY_SETTINGS.forEach((displaySetting) => {
    const param = K.SETTING_TO_URL_PARAM[displaySetting];
    const paramValue = params[param] || '';
    if (paramValue !== '') {
      settingsParams[param] = paramValue;
    }
  });

  return settingsParams;
};
